import { useEffect, useState } from 'react';
import { Plus, Archive, ArchiveRestore, Pencil, Ship, Users, FileText, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import UserEditModal from '@/components/UserEditModal';

export default function Admin({ user }) {
  const [vessels, setVessels] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newVessel, setNewVessel] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [error, setError] = useState('');
  const [tpl, setTpl] = useState({ name: '', case_type: 'Disciplinary', category: 'Conduct', severity: 'Misconduct', summary: '', next_action: 'Review case details' });
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'vessel_hr', vessel: '' });
  const [editingUser, setEditingUser] = useState(null);

  const load = () => Promise.all([
    api('/vessels?include_archived=true').then(setVessels),
    api('/users').then(setUsers),
    api('/case-templates').then(setTemplates),
  ]);
  useEffect(() => { load(); }, []);

  if (user.role !== 'corporate_hr') {
    return <p className="muted" data-testid="admin-forbidden">Corporate HR access required.</p>;
  }

  const addVessel = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api('/vessels', { method: 'POST', body: JSON.stringify({ name: newVessel }) });
      setNewVessel(''); load();
    } catch (err) { setError(err.message); }
  };

  const saveRename = async (id) => {
    try {
      await api(`/vessels/${id}`, { method: 'PATCH', body: JSON.stringify({ name: renameValue }) });
      setRenamingId(null); setRenameValue(''); load();
    } catch (err) { setError(err.message); }
  };

  const toggleArchive = async (v) => {
    const next = v.status === 'active' ? 'archived' : 'active';
    try {
      await api(`/vessels/${v.id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
      load();
    } catch (err) { setError(err.message); }
  };

  const editCode = async (id, code) => {
    try {
      await api(`/vessels/${id}`, { method: 'PATCH', body: JSON.stringify({ code }) });
      load();
    } catch (err) { setError(err.message); }
  };

  const reassign = async (userId, vessel) => {
    try {
      await api(`/users/${userId}/vessel`, { method: 'PATCH', body: JSON.stringify({ vessel: vessel || null }) });
      load();
    } catch (err) { setError(err.message); }
  };

  const reassignManager = async (userId, managerId) => {
    try {
      await api(`/users/${userId}/manager`, { method: 'PATCH', body: JSON.stringify({ manager_id: managerId || null }) });
      load();
    } catch (err) { setError(err.message); }
  };

  const addTemplate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api('/case-templates', { method: 'POST', body: JSON.stringify(tpl) });
      setTpl({ name: '', case_type: 'Disciplinary', category: 'Conduct', severity: 'Misconduct', summary: '', next_action: 'Review case details' });
      load();
    } catch (err) { setError(err.message); }
  };
  const removeTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api(`/case-templates/${id}`, { method: 'DELETE' }); load(); }
    catch (err) { setError(err.message); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = { ...newUser };
      if (payload.role !== 'vessel_hr') payload.vessel = null;
      await api('/users', { method: 'POST', body: JSON.stringify(payload) });
      setNewUser({ name: '', email: '', password: '', role: 'vessel_hr', vessel: '' });
      load();
    } catch (err) { setError(err.message); }
  };

  const active = vessels.filter((v) => v.status === 'active');
  const corporates = users.filter((u) => u.role === 'corporate_hr');

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow">ADMINISTRATION</span>
          <h1>Vessels & assignments</h1>
          <p className="muted">Add ships to your fleet and place the right HR manager on each one.</p>
        </div>
      </div>
      {error && <div className="error" data-testid="admin-error" style={{ marginBottom: 14 }}>{error}</div>}

      <section className="panel">
        <div className="panel-title">
          <div><span className="eyebrow">FLEET</span><h2><Ship size={16} style={{ display: 'inline', marginRight: 6 }} /> Vessels</h2></div>
        </div>
        <form className="toolbar" onSubmit={addVessel} style={{ marginTop: 14 }}>
          <div className="search-box" style={{ maxWidth: 420 }}>
            <Plus size={17} />
            <input data-testid="new-vessel-input" placeholder="Add a vessel (e.g. MV Blue Horizon)" value={newVessel} onChange={(e) => setNewVessel(e.target.value)} required />
          </div>
          <button data-testid="add-vessel-button" type="submit" className="primary-btn"><Plus size={14} /> Add vessel</button>
        </form>
        <div className="admin-list">
          {vessels.length === 0 && <p className="muted">No vessels yet.</p>}
          {vessels.map((v) => (
            <div className="admin-row" key={v.id} data-testid={`vessel-row-${v.id}`}>
              <div>
                {renamingId === v.id ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input data-testid={`rename-input-${v.id}`} className="admin-input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus />
                    <button data-testid={`rename-save-${v.id}`} className="primary-btn" type="button" onClick={() => saveRename(v.id)}>Save</button>
                    <button className="secondary-btn" type="button" onClick={() => setRenamingId(null)}>Cancel</button>
                  </div>
                ) : (
                  <>
                    <span className="vessel-code" data-testid={`vessel-code-${v.id}`}>{v.code || '—'}</span>
                    <strong>{v.name}</strong>
                    <small className="muted"> · {v.status}</small>
                  </>
                )}
              </div>
              {renamingId !== v.id && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button data-testid={`edit-code-${v.id}`} className="secondary-btn" onClick={() => {
                    const next = window.prompt('New 2–5 character vessel code (letters/digits):', v.code || '');
                    if (next) editCode(v.id, next);
                  }}>
                    <Pencil size={12} /> Code
                  </button>
                  <button data-testid={`rename-vessel-${v.id}`} className="secondary-btn" onClick={() => { setRenamingId(v.id); setRenameValue(v.name); }}>
                    <Pencil size={12} /> Rename
                  </button>
                  <button data-testid={`archive-vessel-${v.id}`} className="secondary-btn" onClick={() => toggleArchive(v)}>
                    {v.status === 'active' ? <><Archive size={12} /> Archive</> : <><ArchiveRestore size={12} /> Restore</>}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="panel" style={{ marginTop: 14 }}>
        <div className="panel-title">
          <div><span className="eyebrow">TEAM</span><h2><Users size={16} style={{ display: 'inline', marginRight: 6 }} /> HR managers</h2></div>
        </div>
        <p className="muted" style={{ marginTop: 6 }}>Add corporate HR reviewers or vessel HR managers. New accounts sign in with the temporary password you set.</p>
        <form className="composer grid" onSubmit={addUser} style={{ marginTop: 14 }}>
          <input data-testid="new-user-name" className="admin-input" placeholder="Full name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required />
          <input data-testid="new-user-email" className="admin-input" type="email" placeholder="Email address" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
          <select data-testid="new-user-role" className="admin-input" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value, vessel: e.target.value === 'corporate_hr' ? '' : newUser.vessel })}>
            <option value="vessel_hr">Vessel HR manager</option>
            <option value="corporate_hr">Corporate HR manager</option>
          </select>
          {newUser.role === 'vessel_hr' ? (
            <select data-testid="new-user-vessel" className="admin-input" value={newUser.vessel} onChange={(e) => setNewUser({ ...newUser, vessel: e.target.value })} required>
              <option value="">Assign a vessel…</option>
              {active.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
            </select>
          ) : <span className="muted" style={{ alignSelf: 'center' }}>Fleet-wide access</span>}
          <input data-testid="new-user-password" className="admin-input" type="password" placeholder="Temporary password (min 8 chars)" minLength={8} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
          <button data-testid="add-user-button" type="submit" className="primary-btn"><Plus size={14} /> Add HR manager</button>
        </form>
        <div className="admin-list">
          {users.length === 0 && <p className="muted">No HR users yet.</p>}
          {users.map((u) => (
            <div className="admin-row" key={u.id} data-testid={`user-row-${u.id}`}>
              <div>
                <span className={`role-badge ${u.role}`}>{u.role === 'corporate_hr' ? 'Corporate' : 'Vessel'}</span>
                <strong>{u.name}</strong>
                <small className="muted"> · {u.email}</small>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {u.role === 'vessel_hr' ? (
                  <>
                    <span className="muted">Vessel</span>
                    <select data-testid={`reassign-${u.id}`} className="admin-input" value={u.vessel || ''} onChange={(e) => reassign(u.id, e.target.value)}>
                      <option value="">— Unassigned —</option>
                      {active.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
                    </select>
                    <span className="muted">Manager</span>
                    <select data-testid={`reassign-manager-${u.id}`} className="admin-input" value={u.manager_id || ''} onChange={(e) => reassignManager(u.id, e.target.value)}>
                      <option value="">— Unassigned —</option>
                      {corporates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </>
                ) : <span className="muted">Fleet-wide access</span>}
                <button data-testid={`edit-user-${u.id}`} className="secondary-btn" onClick={() => setEditingUser(u)}>
                  <Pencil size={12} /> Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="panel" style={{ marginTop: 14 }}>
        <div className="panel-title">
          <div><span className="eyebrow">DRAFTS</span><h2><FileText size={16} style={{ display: 'inline', marginRight: 6 }} /> Case templates</h2></div>
        </div>
        <p className="muted" style={{ marginTop: 6 }}>Templates pre-fill the "New case" form so managers can draft in seconds.</p>
        <form className="composer grid" onSubmit={addTemplate} style={{ marginTop: 14 }}>
          <input data-testid="template-name" className="admin-input" placeholder="Template name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} required />
          <select data-testid="template-type" className="admin-input" value={tpl.case_type} onChange={(e) => setTpl({ ...tpl, case_type: e.target.value })}>
            <option>Disciplinary</option><option>Grievance</option>
          </select>
          <input data-testid="template-category" className="admin-input" placeholder="Category" value={tpl.category} onChange={(e) => setTpl({ ...tpl, category: e.target.value })} />
          <select data-testid="template-severity" className="admin-input" value={tpl.severity} onChange={(e) => setTpl({ ...tpl, severity: e.target.value })}>
            <option>Complaint</option><option>Misconduct</option><option>Gross misconduct</option>
          </select>
          <textarea data-testid="template-summary" placeholder="Summary shown to the manager when applied…" value={tpl.summary} onChange={(e) => setTpl({ ...tpl, summary: e.target.value })} />
          <input data-testid="template-next-action" className="admin-input" placeholder="Next action" value={tpl.next_action} onChange={(e) => setTpl({ ...tpl, next_action: e.target.value })} />
          <button data-testid="add-template-button" type="submit" className="primary-btn"><Plus size={14} /> Save template</button>
        </form>
        <div className="admin-list">
          {templates.length === 0 && <p className="muted">No templates yet.</p>}
          {templates.map((t) => (
            <div className="admin-row" key={t.id} data-testid={`template-row-${t.id}`}>
              <div>
                <strong>{t.name}</strong>
                <small className="muted"> · {t.case_type} · {t.severity} · {t.category}</small>
              </div>
              <button data-testid={`delete-template-${t.id}`} className="danger-btn" onClick={() => removeTemplate(t.id)}>
                <Trash2 size={12} /> Delete
              </button>
            </div>
          ))}
        </div>
      </section>
      {editingUser && (
        <UserEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); load(); }}
        />
      )}
    </>
  );
}
